import { CartFund, PRODUCTS, ProductType } from 'cart-model';
import { Fund, FundType, Horizon, Risk, Umbrella } from 'funds-model';
import Screen from '../components/ProductWizard/Screen';
import _ from 'lodash';
import { cartPayload, addToDataLayer, EventType, PRODUCT_TYPE_MAPPING, item } from 'common-event';

import store from './store';

type ChangeInvestmentType = "first" | "monthly";

export function logChangeInvestmentAmount(type: ChangeInvestmentType) {
  addToDataLayer({
    event: EventType.preferenceClickChangeBudget,
    change_type: type
  })
}

export function logSubmitRiskLevel() {
  addToDataLayer({
    event: EventType.preferenceSubmitRiskLevel,
    risk_level: store.getState().uiState.risks.map(risk => risk.value),
    ...userProductContext()
  });
}

export function logChooseInvestment() {
  const state = store.getState().uiState;
  addToDataLayer({
    event: EventType.preferenceSubmitBudget,
    first_payment: state.initialAmount,
    monthly_payment: state.monthlyAmount,
    ...userProductContext()
  });
}

export function logSelectRetirementType() {
  const productType = getProductType();
  addToDataLayer({
    event: EventType.startSelectRetirementType,
    retirement_type: productType ? PRODUCT_TYPE_MAPPING[productType] : undefined,
  });
}

export function logSelectPath(chosenPath: string) {
  addToDataLayer({
    event: EventType.startSelectPath,
    user_path: chosenPath,
    ...userProductContext()
  });
}

export function logSelectProduct(chosenPath: string) {
  addToDataLayer({
    event: EventType.startSelectGoal,
    user_goal: chosenPath,
  });
}

function getScreenNameByCode(sourceScreenCode: string) {
  const SCREENS: Map<Screen, string> = new Map([
    [Screen.SELECT_FUNDS, "expert list"],
    [Screen.SELECT_RECOMMENDED_FUNDS, "preference list"],
    [Screen.SET_AMOUNTS_PER_FUND, 'checkout page'],
    [Screen.SELECT_PATH, "select path page"],
  ]);
  const screenNamesByCode: Map<string, string> = new Map(
        Array.from(SCREENS).map(([screen, name]) => [screen.code, name]))
          .set('basic-data', 'registration page');

  return screenNamesByCode.get(sourceScreenCode);
}

function getScreenName(sourceScreen: Screen) {
  const SCREENS: Map<Screen, string> = new Map([
    [Screen.SELECT_FUNDS, "expert list"],
    [Screen.SELECT_RECOMMENDED_FUNDS, "preference list"],
    [Screen.SET_AMOUNTS_PER_FUND, 'checkout page'],
  ]);
  return SCREENS.get(sourceScreen);
}

export function logFundEvent(eventType: EventType, sourceScreen: Screen, fund: Fund, productType: ProductType, openPopupInMilliseconds?: number) {

  addToDataLayer({
    event: eventType,
    source: getScreenName(sourceScreen),
    ...fundPayload(fund, productType),
    ...popupTime(openPopupInMilliseconds)
  });
}

function popupTime(openPopupInMilliseconds?: number) {
  return openPopupInMilliseconds ? {popupTime: openPopupInMilliseconds} : {};
}

export function logFundPopupEvent(eventType: EventType, sourceScreen: Screen, fund: Fund, productType: ProductType) {
  addToDataLayer({
    event: eventType,
    source: getScreenName(sourceScreen),
    is_popup: "y",
    ...fundPayload(fund, productType)
  });
}

export function logChangeRange(duration: string, sourceScreen: Screen, fund: Fund, productType: ProductType) {
  addToDataLayer({
    event: EventType.productChangeDuration,
    changed_duration: duration,
    source: getScreenName(sourceScreen),
    is_popup: "y",
    ...fundPayload(fund, productType)
  });
}

export function logEvent(eventType: EventType) {
  addToDataLayer({ event: eventType });
}

export function logExpertViewProductList(sourceScreenCode: string) {
  addToDataLayer({
    event: EventType.expertViewProductList,
    ...userProductContext(),
    source: getScreenNameByCode(sourceScreenCode)
  });
}

export function logEventWithChosenProductType(eventType: EventType) {
  addToDataLayer({
    event: eventType,
    ...userProductContext()
  });
}

function getProductType(): ProductType | undefined {
  return store.getState().processMetadataState.selectedTypeOfInvestment;
}

export function logRiskProfilerResult(result: string | null) {
  addToDataLayer({
    event: EventType.riskprofilerComplete,
    profiler_risk_level: result,
    ...userProductContext()
  })
}

export function logRiskProfilerResultExpand(expanded: boolean, level?: number) {
  addToDataLayer({
    event: EventType.riskprofilerClickExpand,
    expand_direction: expanded ? "up" : "down",
    dropdown_name: level ? level : "see_more"
  })
}

export function logSetAmounts(sourceScreen: Screen) {
  const carts: CartFund[] = store.getState().cartState.cartFunds;
  addToDataLayer({
    event: EventType.productClickSetQuota,
    source: getScreenName(sourceScreen),
    ...cartPayload(carts)
  });
}

export function logSubmitSetAmounts() {
  const carts: CartFund[] = store.getState().cartState.cartFunds;
  addToDataLayer({
    event: EventType.setquotaSubmit,
    source: store.getState().cartState.screenWhereUserAddFunds,
    ...cartPayload(carts),
    ...userProductContext()
  });
}

export function logSetAmountsPerFundEntry() {
  const carts: CartFund[] = store.getState().cartState.cartFunds;
  addToDataLayer({
    event: EventType.setquotaView,
    product_count: carts.length,
    fund_id: uniqueArray(carts.map(cart => cart.fund.id)),
    ...userProductContext(),
    order_total: carts.reduce((amount, cart) => amount + cart.amount, 0),
    source: store.getState().cartState.screenWhereUserAddFunds,
  });
  addToDataLayer({
    event: EventType.viewCart,
    ecommerce: {
      value: carts.reduce((amount, cart) => amount + cart.amount, 0),
      currency: 'PLN',
      items: carts.map(cart =>
          ({
            ...item(cart.fund, cart.productType),
            quantity: 1,
            price: cart.amount
          })),
    },
  });
}

export function logChangeCardFundAmount(cartFund: CartFund, oldValue: number) {
  addToDataLayer({
    event: EventType.setquotaEditBudget,
    previous_amount: oldValue,
    fund_id: cartFund.fund.id,
    new_amount: cartFund.amount,
    product_context: PRODUCT_TYPE_MAPPING[cartFund.productType],
  });
  logRemoveFromCart(cartFund.fund, cartFund.productType, undefined, oldValue);
  logAddToCart(cartFund.fund, cartFund.productType, undefined, cartFund.amount);
}

function uniqueArray(array: Array<any>): Array<any> {
  return Array.from(new Set(array));
}

function fundPayload(fund: Fund, productType: ProductType) {
  const unitCategory: string = PRODUCTS[productType].unitCategory;
  return {
    category_unit: unitCategory,
    product_context: PRODUCT_TYPE_MAPPING[productType],
    mgmt_admin_fee: fund.costsForUnitCode[unitCategory] ? fund.costsForUnitCode[unitCategory].ongoingChargePercentage : undefined,
    fund_name: fund.name,
    fund_type: fund.fundType.name,
    fund_id: fund.id.toString(),
    risk_level: [fund.risk.value.toString()],
    analizy_online: fund.analizyOnlineRating ? fund.analizyOnlineRating.toString() : undefined,
    umbrella_name: fund.umbrella ? fund.umbrella.name : undefined,
    fund_price: fund.quotesForUnitCode[unitCategory] ? fund.quotesForUnitCode[unitCategory].value : undefined,
    investment_horizon: fund.horizonValue,
    ...userProductContext(),
  };
}

function userProductContext() {
  const productType = getProductType();
  return productType ?
      { user_product_context: PRODUCT_TYPE_MAPPING[productType] } :
      {};
}

export function logEventStartSearch() {
  addToDataLayer({
    event: EventType.expertStartSearch
  })
}

export function logEventSearch(value: string) {
  addToDataLayer({
    event: EventType.expertSearch,
    search_query: value
  })
}

export function logChangeTypeFilter(oldValues: FundType[], newValues: FundType[]) {
  addToDataLayer({
    event: EventType.expertSetFundType,
    fund_type_list: newValues.map(fundType => fundType.name),
    fund_type: getDifference(newValues, oldValues).name,
    filter_action: newValues.length > oldValues.length ? 'add' : 'remove',
  });
}

function getDifference<T>(newValues: T[], oldValues: T[]) {
  return newValues.length > oldValues.length ?
      _.difference(newValues, oldValues)[0] :
      _.difference(oldValues, newValues)[0];
}

export function logChangeUmbrellaFilter(oldValues: Umbrella[], newValues: Umbrella[]) {
  addToDataLayer({
    event: EventType.expertSetUmbrella,
    umbrella_name: newValues.map(umbrella => umbrella.name),
    umbrella_name_list: getDifference(newValues, oldValues).name,
    filter_action: newValues.length > oldValues.length ? 'add' : 'remove'
  })
}

export function logChangeRiskFilter(oldValues: Risk[], newValues:Risk[]) {
  addToDataLayer({
    event: EventType.expertSetRiskType,
    risk_level_list: newValues.map(risk => risk.value),
    risk_level: getDifference(newValues, oldValues).value,
    filter_action: newValues.length > oldValues.length ? 'add' : 'remove'
  })
}

export function logChangeHorizonFilter(horizon: Horizon) {
  if (horizon) {
    addToDataLayer({
      event: EventType.expertSetInvestmentHorizon,
      inv_horizon_type: horizon.label
    })
  }
}

export function logQuotesChangeRate(newChangeRate: string) {
  addToDataLayer({
    event: EventType.expertSetROI,
    roi_duration: newChangeRate,
  })
}

export type ListingType = 'standard' | 'expert' | 'risk-profiler' | 'preference' | 'similar-funds';

export function itemWithItemListName(fund: Fund, productType: ProductType, listingType?: ListingType, index?: number, listingName?: string) {
  const itemListName = listingType ? `PT:onboarding_LT:${listingType}_LN:${listingName}` : 'standard';
  return item(fund, productType, index, itemListName);
}

function getListingType(sourceScreen: Screen): ListingType | undefined {
  const SCREENS: Map<Screen, ListingType> = new Map([
    [Screen.SELECT_FUNDS, 'expert'],
    [Screen.SELECT_RECOMMENDED_FUNDS, 'preference'],
  ]);
  return SCREENS.get(sourceScreen);
}

export function logSelectItemEvent(fund: Fund, productType: ProductType, sourceScreen: Screen, index: number, riskProfilerLevel?: number) {
  if (sourceScreen === Screen.SET_AMOUNTS_PER_FUND) {
    return;
  }
  addToDataLayer({
    event: EventType.selectItem,
    ecommerce: {
      items: [
        itemWithItemListName(fund, productType, getListingType(sourceScreen), index, getListingName(riskProfilerLevel)),
      ],
    },
  });
  addToDataLayer({
    event: EventType.viewItem,
    ecommerce: {
      items: [
        itemWithItemListName(fund, productType),
      ],
    },
  });
}

export function getListingName(riskProfilerLevel?: number) {
  return riskProfilerLevel ? `risk-level-${riskProfilerLevel}` : 'standard';
}

export function logViewItemEvent(funds: (Fund & {productType: ProductType, index: number})[], sourceScreen: Screen, riskProfilerLevel?: number) {
  addToDataLayer({
    event: EventType.viewItemList,
    ecommerce: {
      items: funds.map(fund =>
          itemWithItemListName(fund, fund.productType, getListingType(sourceScreen), fund.index, getListingName(riskProfilerLevel))),
    },
  });
}

const cartValueOrInitialValue = (productType: ProductType, value?: number) => value ? value : PRODUCTS[productType].initialAmount;

export function logAddToCart(fund: Fund, productType: ProductType, sourceScreen?: Screen, value?: number) {
  addToDataLayer({
    event: EventType.addToCart,
    ecommerce: {
      value: cartValueOrInitialValue(productType, value),
      currency: 'PLN',
      items: [{
        ...itemWithItemListName(fund, productType, sourceScreen ? getListingType(sourceScreen) : undefined),
        quantity: 1,
        price: cartValueOrInitialValue(productType, value),
      }],
    },
  });
}

export function logRemoveFromCart(fund: Fund, productType: ProductType, sourceScreen?: Screen, value?: number) {
  addToDataLayer({
    event: EventType.removeFromCart,
    ecommerce: {
      value: cartValueOrInitialValue(productType, value),
      currency: 'PLN',
      items: [{
        ...itemWithItemListName(fund, productType, sourceScreen ? getListingType(sourceScreen) : undefined),
        quantity: 1,
        price: cartValueOrInitialValue(productType, value),
      }],
    },
  });
}
